import { AddressAutocomplete } from '@telia-common-components/address';
import { AddressEvents } from '@telia-common-components/events';
import { EnvironmentContext, LanguageContext, TranslationsContext } from '@telia-common-components/shared';
import * as React from 'react';
import { Container, Grid, GridColumn, Modal, ModalProvider, scrollToElement, Separator, useAnalytics } from 'telia-front-react';
import { FetchStatus } from '../../../util/FetchStatus';
import { InternetDetailsModal } from './details/InternetDetailsModal';
import { useInternetCalculator } from './InternetCalculatorProvider';
import { DEFAULT_SELECTED_PACKAGE_STATE, useInternetNotices } from './InternetNoticesProvider';
import { useInternetOffers } from './InternetOffersProvider';
import ExtraInformationAndCards from './jsx/ExtraInformationAndCards';
import { CustomerNotices, NOTICES_ABOVE_SPEED_SUGGEST_CALC_DIV_ID } from './notice/CustomerNotices';
import { InternetOversoldWarning } from './notice/InternetOversoldWarning';
import { InternetSuggestSpeedCalculator } from './suggest/InternetSuggestSpeedCalculator';
import { getIsDealerFromAuth, getCustomerIdFromAuth, getSegment, isDealerWithoutCustomer, resolveAddressCode } from './util/InternetCalculatorUtil';
import { InternetArgosOrderNotice } from './notice/InternetArgosOrderNotice';
import OfferCarousel from './OfferCarousel';
import AddressNotFoundModal from './securenet/AddressNotFoundModal';
import AddressSearchPopUp from './AddressSearchPopUp';
const InternetCalculatorContent = (props) => {
    var _a, _b;
    const { disableTranslationsStatistics, isTestEnvironment } = props;
    const { app, tier } = React.useContext(EnvironmentContext);
    const { lang } = React.useContext(LanguageContext);
    const { push } = useAnalytics();
    const { translations } = React.useContext(TranslationsContext);
    const mounted = React.useRef();
    const { fetchOffersByLocation, onAddressChange, addressProductsState, calculatorState } = useInternetOffers();
    const { setModalState, auth, fetchInternetCalculatorConfig, fetchInternetCalculatorExtraCards, fetchSecureNetCalculatorExtraCards, modalState, isSecureNet } = useInternetCalculator();
    const { setSelectedInternetPackage, fetchHasLegalOrServiceMandate, fetchRepresentationStipulation, hasLegalOrServiceMandateState, creditAllowedState, hasCreditOrDebtErrors, selectedInternetPackage, fetchIsCreditAllowed, resetIsCreditAllowed, isSsoModalOpen } = useInternetNotices();
    const isDealer = getIsDealerFromAuth(auth);
    const customerId = getCustomerIdFromAuth(auth);
    const segment = getSegment(auth);
    const offerCards = calculatorState.internetOffers.internetOfferCards;
    React.useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
        }
        else {
            fetchOffersByLocation(calculatorState.address, calculatorState.room);
        }
    }, [customerId, isDealer, segment]);
    React.useEffect(() => {
        fetchInternetCalculatorConfig();
    }, []);
    React.useEffect(() => {
        fetchHasLegalOrServiceMandate();
        if (isSecureNet) {
            fetchRepresentationStipulation();
        }
    }, [customerId]);
    React.useEffect(() => {
        fetchIsCreditAllowed();
        return () => {
            resetIsCreditAllowed();
        };
    }, [hasLegalOrServiceMandateState.status, customerId]);
    React.useEffect(() => {
        redirectOrRemoveSelectedPackageIfNecessary();
    }, [customerId, hasLegalOrServiceMandateState.status, addressProductsState.status, creditAllowedState.status, selectedInternetPackage === null || selectedInternetPackage === void 0 ? void 0 : selectedInternetPackage.url]);
    React.useEffect(() => {
        isSecureNet ? fetchSecureNetCalculatorExtraCards(lang) : fetchInternetCalculatorExtraCards(lang);
    }, [lang]);
    const pushGaAndSubmit = async (address, room) => {
        push(`Show - Address - ${resolveAddressCode(address, room)}`);
        await onAddressChange(address, room);
        scrollToElement(NOTICES_ABOVE_SPEED_SUGGEST_CALC_DIV_ID);
    };
    const redirectAndPushAnalytics = () => {
        const selectedPackageUrl = selectedInternetPackage.url;
        push(`CTA - Order - ${selectedInternetPackage.productOfferingCode}`);
        setSelectedInternetPackage(DEFAULT_SELECTED_PACKAGE_STATE);
        window.location.assign(selectedPackageUrl);
    };
    const redirectOrRemoveSelectedPackageIfNecessary = () => {
        var _a;
        const isAllFetched = addressProductsState.status === FetchStatus.FETCHED &&
            creditAllowedState.status === FetchStatus.FETCHED &&
            hasLegalOrServiceMandateState.status === FetchStatus.FETCHED;
        const hasValidConditions = !!calculatorState.address &&
            ((segment === 'business' && isSecureNet) || (segment === 'private' && !isSecureNet)) &&
            (!!selectedInternetPackage.url || selectedInternetPackage.hasForm) &&
            !hasCreditOrDebtErrors() &&
            !isDealerWithoutCustomer(isDealer, customerId) &&
            !((_a = addressProductsState === null || addressProductsState === void 0 ? void 0 : addressProductsState.addressProducts) === null || _a === void 0 ? void 0 : _a.addressHasProducts) &&
            hasLegalOrServiceMandateState.hasMandate;
        if ((!!customerId || isSecureNet) && isAllFetched && hasValidConditions) {
            if (!!selectedInternetPackage.url && !!customerId) {
                redirectAndPushAnalytics();
            }
            else if (selectedInternetPackage.hasForm) {
                setModalState({ offer: selectedInternetPackage.offer, showForm: true });
                setSelectedInternetPackage(DEFAULT_SELECTED_PACKAGE_STATE);
            }
        }
        else if (isAllFetched && (!!selectedInternetPackage.url || !!selectedInternetPackage.hasForm)) {
            setSelectedInternetPackage(DEFAULT_SELECTED_PACKAGE_STATE);
        }
    };
    const isLoading = (customerId && hasLegalOrServiceMandateState.status === FetchStatus.FETCHING) || !(translations === null || translations === void 0 ? void 0 : translations.iocc);
    const AddressSearchIssuesButtons = () => {
        var _a, _b;
        return (React.createElement(Grid, { noWrap: true, align: ['center-xs'] },
            React.createElement(GridColumn, { width: ['min'] },
                React.createElement(Separator, { color: "invisible", spacing: "xsmall" }),
                React.createElement(AddressNotFoundModal, { isLoading: isLoading })),
            isDealer && (React.createElement(GridColumn, { width: ['min'] },
                React.createElement(Separator, { color: "invisible", spacing: "xsmall" }),
                React.createElement(AddressSearchPopUp, { tier: tier, addressId: (_a = calculatorState.address) === null || _a === void 0 ? void 0 : _a.id, roomId: (_b = calculatorState.room) === null || _b === void 0 ? void 0 : _b.roomId })))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, null,
            React.createElement(AddressAutocomplete, { app: app, tier: tier, language: lang, onSubmit: pushGaAndSubmit, autoSubmit: true, disableTranslationsStatistics: disableTranslationsStatistics, isTestEnvironment: isTestEnvironment, address: calculatorState.address, room: calculatorState.room, roomLabel: isSecureNet ? 'addressAutocomplete.room.B2B' : calculatorState.roomLabel, isLoading: isLoading }),
            isSecureNet && React.createElement(AddressSearchIssuesButtons, null),
            isDealer && calculatorState.address && (React.createElement(React.Fragment, null,
                React.createElement(Separator, { spacing: "xsmall", color: "invisible" }),
                React.createElement(AddressEvents, { app: app, tier: tier, addressParams: {
                        addressId: calculatorState.address.id,
                        buildingId: calculatorState.address.buildingId,
                        addressText: calculatorState.address.label,
                        roomId: calculatorState.room && calculatorState.room.roomId
                    }, buttonProps: { type: 'employee', size: 'small' }, showHeading: false, showEmpty: false, showAddNewAddressEvent: true, disableTranslationsStatistics: disableTranslationsStatistics }))),
            React.createElement(CustomerNotices, null),
            React.createElement(InternetSuggestSpeedCalculator, { hidden: !calculatorState.address, offerCards: offerCards }),
            React.createElement(InternetArgosOrderNotice, { internetOfferCards: offerCards, offerFetchStatus: calculatorState.status, isDealer: isDealer }),
            !isSecureNet && React.createElement(InternetOversoldWarning, { internetOfferCards: offerCards, isDealer: isDealer })),
        React.createElement(Separator, { color: "invisible", spacing: "xsmall" }),
        React.createElement(OfferCarousel, null),
        React.createElement(ModalProvider, null,
            React.createElement(Modal, { isOpen: !isSsoModalOpen && !!(modalState === null || modalState === void 0 ? void 0 : modalState.offer), onClose: () => setModalState({ offer: null, showForm: false }), size: (modalState === null || modalState === void 0 ? void 0 : modalState.showTelia1PromoModal)
                    ? '6-col'
                    : modalState.showForm &&
                        !(((_a = modalState.offer) === null || _a === void 0 ? void 0 : _a.properties.includes('PARTNER')) ||
                            (((_b = modalState.offer) === null || _b === void 0 ? void 0 : _b.properties.includes('SECURE_NET')) && modalState.offer.properties.includes('PLANNED')))
                        ? '10-col'
                        : undefined },
                React.createElement(InternetDetailsModal, null))),
        React.createElement(Container, null,
            React.createElement(ExtraInformationAndCards, { showRequestForm: calculatorState.showRequestForm, addressSelected: !!calculatorState.address, isLoading: isLoading }))));
};
export default InternetCalculatorContent;
